import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Tooltip, Select, MenuItem,
  InputBase, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { bloomberg } from '../theme/bloombergTheme';
import api from '../services/api';
import { useLivePrices } from '../hooks/useLivePrices';
import { formatPrice } from '../utils/formatCurrency';

// ── Types ─────────────────────────────────────────────────────────────────────

type MarketStatus = 'open' | 'pre-market' | 'after-hours' | 'closed';

interface PortfolioRef { id: number; name: string; }

interface UniverseItem {
  symbol: string;
  company_name: string;
  exchange: string;
  country: string;
  currency: string;
  sector: string;
  price: number | null;
  chg_1d: number | null;
  market_status: MarketStatus;
  portfolios: PortfolioRef[];
}

interface UniverseData {
  items: UniverseItem[];
  last_updated: string | null;
}

type SortKey = 'symbol' | 'company_name' | 'exchange' | 'sector' | 'price' | 'chg_1d' | 'market_status';

// ── Constants ─────────────────────────────────────────────────────────────────

const REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

const STATUS_ORDER: MarketStatus[] = ['open', 'pre-market', 'after-hours', 'closed'];

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: MarketStatus }) {
  if (status === 'open') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: '#22c55e',
          boxShadow: '0 0 5px #22c55e',
          display: 'inline-block',
        }} />
        <span style={{ fontSize: 10, color: '#22c55e', fontWeight: 600 }}>OPEN</span>
      </span>
    );
  }
  if (status === 'pre-market') {
    return (
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '2px 5px',
        borderRadius: 3, backgroundColor: 'rgba(251,191,36,0.15)',
        color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)',
        letterSpacing: '0.05em',
      }}>PRE</span>
    );
  }
  if (status === 'after-hours') {
    return (
      <span style={{
        fontSize: 9, fontWeight: 700, padding: '2px 5px',
        borderRadius: 3, backgroundColor: 'rgba(99,179,237,0.15)',
        color: '#63b3ed', border: '1px solid rgba(99,179,237,0.3)',
        letterSpacing: '0.05em',
      }}>AH</span>
    );
  }
  // closed
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        backgroundColor: bloomberg.text.muted,
        display: 'inline-block',
      }} />
      <span style={{ fontSize: 10, color: bloomberg.text.muted }}>CLOSED</span>
    </span>
  );
}

// ── Countdown timer ───────────────────────────────────────────────────────────

function Countdown({ nextRefreshAt }: { nextRefreshAt: number }) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const tick = () => setRemaining(Math.max(0, Math.ceil((nextRefreshAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextRefreshAt]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  return (
    <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, color: bloomberg.text.muted }}>
      next refresh {mins}:{secs.toString().padStart(2, '0')}
    </span>
  );
}

// ── Sort header cell ──────────────────────────────────────────────────────────

function SortTh({
  label, col, sortCol, sortDir, onSort, style,
}: {
  label: string; col: SortKey; sortCol: SortKey; sortDir: 'asc' | 'desc';
  onSort: (c: SortKey) => void; style?: React.CSSProperties;
}) {
  const active = sortCol === col;
  return (
    <th
      onClick={() => onSort(col)}
      style={{
        padding: '5px 8px', fontSize: 9, fontWeight: 700, textAlign: 'left',
        color: active ? bloomberg.accent.orange : bloomberg.text.muted,
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
        textTransform: 'uppercase', letterSpacing: '0.06em',
        borderBottom: `1px solid ${bloomberg.border.primary}`,
        background: bloomberg.bg.secondary,
        ...style,
      }}
    >
      {label}
      {active && (
        sortDir === 'asc'
          ? <ArrowUpwardIcon sx={{ fontSize: 9, ml: 0.3, verticalAlign: 'middle' }} />
          : <ArrowDownwardIcon sx={{ fontSize: 9, ml: 0.3, verticalAlign: 'middle' }} />
      )}
    </th>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function LiveUniverse() {
  const navigate = useNavigate();
  const [data, setData] = useState<UniverseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextRefreshAt, setNextRefreshAt] = useState(Date.now() + REFRESH_INTERVAL_MS);

  // Filters
  const [search, setSearch] = useState('');
  const [portfolioFilter, setPortfolioFilter] = useState<number | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<MarketStatus | 'all'>('all');

  // Sort
  const [sortCol, setSortCol] = useState<SortKey>('company_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Live price stream (SSE) — pushes updates + per-symbol flash signals.
  const { items: liveItems, lastUpdated: liveUpdated, connected, flashes } = useLivePrices();

  const fetchData = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const { data: res } = await api.get('/api/model-portfolios/universe/live-prices');
      setData(res);
      setError(null);
      setNextRefreshAt(Date.now() + REFRESH_INTERVAL_MS);
    } catch (e: any) {
      setError('Failed to load universe data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial fetch for instant first paint (SSE snapshot arrives a beat later).
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Once the SSE stream has data, it becomes the source of truth — every
  // cache refresh pushes here with zero polling.
  useEffect(() => {
    if (liveItems.length > 0) {
      setData({ items: liveItems as any, last_updated: liveUpdated });
      setLoading(false);
      setError(null);
      setNextRefreshAt(Date.now() + REFRESH_INTERVAL_MS);
    }
  }, [liveItems, liveUpdated]);

  const handleSort = (col: SortKey) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // Derive unique portfolios for filter dropdown
  const allPortfolios = useMemo(() => {
    if (!data) return [];
    const map = new Map<number, string>();
    for (const item of data.items) {
      for (const p of item.portfolios) {
        map.set(p.id, p.name);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // Status counts for filter chips
  const statusCounts = useMemo(() => {
    if (!data) return {} as Record<string, number>;
    const counts: Record<string, number> = { all: data.items.length };
    for (const item of data.items) {
      counts[item.market_status] = (counts[item.market_status] || 0) + 1;
    }
    return counts;
  }, [data]);

  // Filtered + sorted items
  const displayItems = useMemo(() => {
    if (!data) return [];
    let items = data.items;

    if (portfolioFilter !== 'all') {
      items = items.filter(i => i.portfolios.some(p => p.id === portfolioFilter));
    }
    if (statusFilter !== 'all') {
      items = items.filter(i => i.market_status === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      items = items.filter(i =>
        i.symbol.toLowerCase().includes(q) ||
        i.company_name.toLowerCase().includes(q) ||
        i.sector.toLowerCase().includes(q)
      );
    }

    items = [...items].sort((a, b) => {
      let av: any, bv: any;
      switch (sortCol) {
        case 'symbol':        av = a.symbol;        bv = b.symbol; break;
        case 'company_name':  av = a.company_name;  bv = b.company_name; break;
        case 'exchange':      av = a.exchange;      bv = b.exchange; break;
        case 'sector':        av = a.sector;        bv = b.sector; break;
        case 'price':         av = a.price ?? -Infinity; bv = b.price ?? -Infinity; break;
        case 'chg_1d':        av = a.chg_1d ?? -Infinity; bv = b.chg_1d ?? -Infinity; break;
        case 'market_status':
          av = STATUS_ORDER.indexOf(a.market_status);
          bv = STATUS_ORDER.indexOf(b.market_status);
          break;
        default:              av = ''; bv = '';
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return items;
  }, [data, portfolioFilter, statusFilter, search, sortCol, sortDir]);

  const openCount = statusCounts['open'] || 0;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 0 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 1.5, flexWrap: 'wrap', gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{
            fontSize: 13, fontWeight: 700, color: bloomberg.text.primary,
            textTransform: 'uppercase', letterSpacing: '0.08em',
          }}>
            Latest Prices
          </Typography>
          <Tooltip title={connected ? 'Live stream connected — prices push automatically' : 'Reconnecting to live stream…'}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 0.75, py: '2px', borderRadius: '4px',
              border: `1px solid ${connected ? 'rgba(34,197,94,0.4)' : bloomberg.border.primary}`,
              bgcolor: connected ? 'rgba(34,197,94,0.10)' : 'transparent',
            }}>
              <Box
                className={connected ? 'bb-live-dot' : undefined}
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: connected ? '#22c55e' : bloomberg.text.muted }}
              />
              <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: connected ? '#22c55e' : bloomberg.text.muted }}>
                {connected ? 'LIVE' : 'OFFLINE'}
              </Typography>
            </Box>
          </Tooltip>
          {data && (
            <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
              {displayItems.length} of {data.items.length} stocks
              {openCount > 0 && (
                <Box component="span" sx={{ ml: 1, color: '#22c55e' }}>
                  · {openCount} open
                </Box>
              )}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {data?.last_updated && (
            <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, fontFamily: '"IBM Plex Mono", monospace' }}>
              updated {new Date(data.last_updated + 'Z').toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          <Countdown nextRefreshAt={nextRefreshAt} />
          <Tooltip title="Refresh now">
            <IconButton
              size="small"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              sx={{ p: 0.4, color: bloomberg.text.muted, '&:hover': { color: bloomberg.accent.orange } }}
            >
              {refreshing
                ? <CircularProgress size={13} sx={{ color: bloomberg.accent.orange }} />
                : <RefreshIcon sx={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        {/* Search */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75,
          bgcolor: bloomberg.bg.secondary,
          border: `1px solid ${bloomberg.border.primary}`,
          borderRadius: '4px', px: 1, py: 0.4, width: 200,
        }}>
          <SearchIcon sx={{ fontSize: 13, color: bloomberg.text.muted }} />
          <InputBase
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search symbol, name…"
            sx={{ fontSize: '0.72rem', color: bloomberg.text.primary, '& input': { p: 0 } }}
          />
          {search && (
            <Box component="span" onClick={() => setSearch('')}
              sx={{ fontSize: 12, color: bloomberg.text.muted, cursor: 'pointer', '&:hover': { color: bloomberg.text.primary } }}>
              ✕
            </Box>
          )}
        </Box>

        {/* Portfolio filter */}
        <Select
          value={portfolioFilter}
          onChange={e => setPortfolioFilter(e.target.value as number | 'all')}
          size="small"
          sx={{
            fontSize: '0.72rem', color: bloomberg.text.primary,
            bgcolor: bloomberg.bg.secondary,
            border: `1px solid ${bloomberg.border.primary}`,
            borderRadius: '4px', height: 28,
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiSelect-icon': { color: bloomberg.text.muted, fontSize: 16 },
            minWidth: 160,
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                bgcolor: bloomberg.bg.secondary,
                border: `1px solid ${bloomberg.border.primary}`,
                '& .MuiMenuItem-root': {
                  fontSize: '0.72rem', color: bloomberg.text.primary,
                  '&:hover': { bgcolor: bloomberg.bg.hover },
                  '&.Mui-selected': { bgcolor: bloomberg.bg.hover, color: bloomberg.accent.orange },
                },
              },
            },
          }}
        >
          <MenuItem value="all">All portfolios ({allPortfolios.length})</MenuItem>
          {allPortfolios.map(p => (
            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
          ))}
        </Select>

        {/* Status filter chips */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {(['all', 'open', 'pre-market', 'after-hours', 'closed'] as const).map(s => {
            const count = s === 'all' ? (data?.items.length || 0) : (statusCounts[s] || 0);
            if (s !== 'all' && count === 0) return null;
            const isActive = statusFilter === s;
            const colors: Record<string, string> = {
              all: bloomberg.accent.orange,
              'open': '#22c55e',
              'pre-market': '#fbbf24',
              'after-hours': '#63b3ed',
              'closed': bloomberg.text.muted,
            };
            const color = colors[s];
            return (
              <Box
                key={s}
                onClick={() => setStatusFilter(s)}
                sx={{
                  px: 1, py: 0.3, borderRadius: '3px', cursor: 'pointer',
                  fontSize: 10, fontWeight: isActive ? 700 : 400,
                  border: `1px solid ${isActive ? color : bloomberg.border.primary}`,
                  color: isActive ? color : bloomberg.text.muted,
                  bgcolor: isActive ? `${color}18` : 'transparent',
                  transition: 'all 0.15s',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  '&:hover': { borderColor: color, color: color },
                }}
              >
                {s === 'all' ? 'All' : s} {count}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress size={28} sx={{ color: bloomberg.accent.orange }} />
        </Box>
      ) : error ? (
        <Typography sx={{ color: bloomberg.status.negative, fontSize: 12, p: 2 }}>{error}</Typography>
      ) : (
        <Box sx={{
          border: `1px solid ${bloomberg.border.primary}`,
          borderRadius: '4px',
          overflow: 'hidden',
        }}>
          <Box sx={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '9%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '16%' }} />
              </colgroup>
              <thead>
                <tr>
                  <SortTh label="Status" col="market_status" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <SortTh label="Name" col="company_name" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <SortTh label="Ticker" col="symbol" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ whiteSpace: 'nowrap', width: '1%' }} />
                  <SortTh label="1D %" col="chg_1d" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ whiteSpace: 'nowrap', width: '1%', textAlign: 'center' }} />
                  <SortTh label="Sector" col="sector" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <SortTh label="Exchange" col="exchange" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                  <SortTh label="Price" col="price" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} style={{ textAlign: 'right' }} />
                  <th style={{
                    padding: '5px 8px', fontSize: 9, fontWeight: 700, textAlign: 'left',
                    color: bloomberg.text.muted, whiteSpace: 'nowrap',
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                    borderBottom: `1px solid ${bloomberg.border.primary}`,
                    background: bloomberg.bg.secondary,
                  }}>Portfolios</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ padding: '24px', textAlign: 'center', color: bloomberg.text.muted, fontSize: 12 }}>
                      No stocks match the current filters
                    </td>
                  </tr>
                ) : displayItems.map((item, idx) => {
                  const isEven = idx % 2 === 0;
                  const chgColor = item.chg_1d == null
                    ? bloomberg.text.muted
                    : item.chg_1d >= 0
                      ? bloomberg.status.positive
                      : bloomberg.status.negative;

                  return (
                    <tr
                      key={item.symbol}
                      style={{ backgroundColor: isEven ? 'transparent' : bloomberg.bg.secondary }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = bloomberg.bg.hover)}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = isEven ? 'transparent' : bloomberg.bg.secondary)}
                    >
                      {/* Status */}
                      <td style={{ padding: '5px 8px' }}>
                        <StatusBadge status={item.market_status} />
                      </td>

                      {/* Name */}
                      <td style={{ padding: '5px 8px', overflow: 'hidden' }}>
                        <Tooltip title={item.company_name} placement="top" arrow enterDelay={300}>
                          <span style={{
                            fontSize: 11, color: bloomberg.text.primary,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            display: 'block',
                          }}>
                            {item.company_name}
                          </span>
                        </Tooltip>
                      </td>

                      {/* Symbol */}
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap', width: '1%' }}>
                        <span
                          onClick={() => navigate(`/stock/${item.symbol}`)}
                          style={{
                            fontSize: 11, fontWeight: 700,
                            color: bloomberg.accent.orange,
                            fontFamily: '"IBM Plex Mono", monospace',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                          onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                        >
                          {item.symbol}
                        </span>
                      </td>

                      {/* 1D change */}
                      <td style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap', width: '1%' }}>
                        {item.chg_1d != null ? (
                          <span style={{
                            fontSize: 11, fontWeight: 600,
                            color: chgColor,
                            fontFamily: '"IBM Plex Mono", monospace',
                            ...(Math.abs(item.chg_1d) >= 5 ? {
                              border: `1px solid ${item.chg_1d >= 0 ? bloomberg.status.positive : bloomberg.status.negative}`,
                              borderRadius: 3,
                              padding: '1px 4px',
                            } : {}),
                          }}>
                            {`${item.chg_1d >= 0 ? '+' : ''}${item.chg_1d.toFixed(2)}%`}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: bloomberg.text.muted, fontFamily: '"IBM Plex Mono", monospace' }}>—</span>
                        )}
                      </td>

                      {/* Sector */}
                      <td style={{ padding: '5px 8px', overflow: 'hidden' }}>
                        <Tooltip title={item.sector} placement="top" arrow enterDelay={400}>
                          <span style={{
                            fontSize: 10, color: bloomberg.text.secondary,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            display: 'block',
                          }}>
                            {item.sector || '—'}
                          </span>
                        </Tooltip>
                      </td>

                      {/* Exchange */}
                      <td style={{ padding: '5px 8px' }}>
                        <span style={{ fontSize: 10, color: bloomberg.text.muted }}>
                          {item.exchange || item.country || '—'}
                        </span>
                      </td>

                      {/* Price */}
                      <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                        <span
                          key={`${item.symbol}-${flashes[item.symbol] || 'none'}-${item.price}`}
                          className={flashes[item.symbol] ? `bb-flash-${flashes[item.symbol]}` : undefined}
                          style={{
                            fontSize: 11, color: bloomberg.text.primary,
                            fontFamily: '"IBM Plex Mono", monospace',
                            padding: '1px 3px',
                          }}
                        >
                          {item.price != null
                            ? formatPrice(item.price, item.currency)
                            : '—'}
                        </span>
                      </td>

                      {/* Portfolios */}
                      <td style={{ padding: '5px 8px', overflow: 'hidden' }}>
                        <Tooltip
                          title={item.portfolios.map(p => p.name).join(', ')}
                          placement="top"
                          arrow
                          enterDelay={300}
                        >
                          <span style={{
                            fontSize: 10, color: bloomberg.text.secondary,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            display: 'block',
                          }}>
                            {item.portfolios.length === 1
                              ? item.portfolios[0].name
                              : `${item.portfolios.length} portfolios`}
                          </span>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>
      )}
    </Box>
  );
}
